import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, Plus, Search } from "lucide-react";
import { getQueryEmployees, getQueryTickets } from "./clientQueryService";
import { buttonClasses, inputClasses, primaryClasses } from "./queryUi";

export default function QueryTaskForm({ query, busy, onSubmit, onCancel }) {
  const [employees, setEmployees] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(true);
  const [employeeError, setEmployeeError] = useState("");
  const [ticketResult, setTicketResult] = useState({ items: [], hasNextPage: false });
  const [ticketLoading, setTicketLoading] = useState(true);
  const [ticketError, setTicketError] = useState("");
  const [search, setSearch] = useState("");
  const [ticketPage, setTicketPage] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [employeeId, setEmployeeId] = useState("");
  const [validation, setValidation] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setEmployeeLoading(true);
    setEmployeeError("");
    getQueryEmployees(controller.signal).then((items) => { if (!controller.signal.aborted) setEmployees(items); }).catch((error) => {
      if (!controller.signal.aborted) setEmployeeError(error.message);
    }).finally(() => { if (!controller.signal.aborted) setEmployeeLoading(false); });
    return () => controller.abort();
  }, [retry]);

  useEffect(() => {
    const controller = new AbortController();
    setTicketLoading(true);
    setTicketError("");
    const timer = setTimeout(() => {
      getQueryTickets({ searchText: search.trim(), page: ticketPage, signal: controller.signal })
        .then((result) => { if (!controller.signal.aborted) setTicketResult(result); })
        .catch((error) => { if (!controller.signal.aborted) setTicketError(error.message); })
        .finally(() => { if (!controller.signal.aborted) setTicketLoading(false); });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [search, ticketPage, retry]);

  const submit = (event) => {
    event.preventDefault();
    if (!selectedTicket || !employeeId) { setValidation("Choose a ticket and an employee."); return; }
    setValidation("");
    onSubmit({ ticketId: selectedTicket.id, assignedEmployeeId: employeeId });
  };
  const tickets = selectedTicket && !ticketResult.items.some((item) => String(item.id) === String(selectedTicket.id))
    ? [selectedTicket, ...ticketResult.items] : ticketResult.items;

  return (
    <form aria-label={`Task assignment for ${query.queryTitle}`} onSubmit={submit} className="mt-6 space-y-4 rounded-2xl border border-orange-200 bg-orange-50/40 p-4 dark:border-orange-900/40 dark:bg-orange-950/10">
      <div>
        <h3 className="font-bold text-[#4A1B13] dark:text-orange-100">Create a task</h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">The task will use this query’s title, description, and attachments.</p>
      </div>
      {(employeeError || ticketError) && <div role="alert" className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">{employeeError || ticketError} <button type="button" className="ml-2 underline" disabled={busy} onClick={() => setRetry((value) => value + 1)}>Retry loading options</button></div>}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="query-ticket-search" className="block text-xs font-bold uppercase tracking-wide text-slate-500">Search tickets</label>
          <div className="relative"><Search size={16} className="pointer-events-none absolute left-3 top-3 text-slate-400" /><input id="query-ticket-search" value={search} maxLength={250} disabled={busy} onChange={(event) => { setSearch(event.target.value); setTicketPage(1); }} className={`${inputClasses} pl-9`} placeholder="Ticket number, subject, or customer" /></div>
          <label htmlFor="query-ticket" className="block text-xs font-bold uppercase tracking-wide text-slate-500">Link to ticket <span className="text-rose-500">*</span></label>
          <select id="query-ticket" value={selectedTicket?.id || ""} disabled={busy || ticketLoading || Boolean(ticketError)} onChange={(event) => setSelectedTicket(tickets.find((item) => String(item.id) === event.target.value) || null)} className={inputClasses}>
            <option value="">{ticketLoading ? "Loading tickets…" : "Choose a ticket"}</option>
            {tickets.map((ticket) => <option key={ticket.id} value={ticket.id}>{ticket.number || `#${ticket.id}`} — {ticket.subject}{ticket.customerName ? ` (${ticket.customerName})` : ""}</option>)}
          </select>
          {!ticketLoading && !ticketError && ticketResult.items.length === 0 && <p className="text-xs text-slate-500">No tickets match this search.</p>}
          <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
            <button type="button" aria-label="Previous ticket page" className="rounded-lg p-1.5 hover:bg-orange-100 disabled:opacity-40 dark:hover:bg-gray-800" disabled={busy || ticketLoading || ticketPage === 1} onClick={() => setTicketPage((value) => value - 1)}><ArrowLeft size={15} /></button>
            <span>Ticket page {ticketPage}</span>
            <button type="button" aria-label="Next ticket page" className="rounded-lg p-1.5 hover:bg-orange-100 disabled:opacity-40 dark:hover:bg-gray-800" disabled={busy || ticketLoading || Boolean(ticketError) || !ticketResult.hasNextPage} onClick={() => setTicketPage((value) => value + 1)}><ArrowRight size={15} /></button>
          </div>
        </div>
        <div>
          <label htmlFor="query-assignee" className="mb-2 block text-xs font-bold uppercase tracking-wide text-slate-500">Assign to employee <span className="text-rose-500">*</span></label>
          <select id="query-assignee" value={employeeId} disabled={busy || employeeLoading || Boolean(employeeError)} onChange={(event) => setEmployeeId(event.target.value)} className={inputClasses}>
            <option value="">{employeeLoading ? "Loading employees…" : "Choose an employee"}</option>
            {employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}{employee.code ? ` (${employee.code})` : ""}</option>)}
          </select>
          <p className="mt-2 text-xs text-slate-500 dark:text-gray-400">Active employees with a linked login account are available.</p>
          {!employeeLoading && !employeeError && employees.length === 0 && <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">No eligible employees are available.</p>}
        </div>
      </div>
      {validation && <p role="alert" className="text-sm font-medium text-rose-600 dark:text-rose-300">{validation}</p>}
      <div className="flex flex-wrap justify-end gap-2 border-t border-orange-100 pt-4 dark:border-gray-800">
        <button type="button" onClick={onCancel} disabled={busy} className={buttonClasses}>Cancel</button>
        <button type="submit" className={primaryClasses} disabled={busy || employeeLoading || ticketLoading || Boolean(employeeError || ticketError) || !employees.length}>{busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} {busy ? "Creating task…" : "Create task"}</button>
      </div>
    </form>
  );
}
